import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Copy, Package, Plus, TrashIcon } from "@/components/icons";
import {
  Badge,
  Button,
  confirm,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Skeleton,
  toast,
} from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import type { GoodsReceiptStatus } from "@/server/platform/db/schema/inventory";
import { formatLak } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  useDeleteGoodsReceipt,
  useDuplicateGoodsReceipt,
  useGoodsReceiptsQuery,
} from "../api/queries";
import { InventoryPlanningPanel } from "../ui/InventoryPlanningPanel";

const STATUS_LABELS: Record<GoodsReceiptStatus, string> = {
  draft: "ຮ່າງ",
  confirmed: "ຢືນຢັນແລ້ວ",
  void: "ຍົກເລີກ",
};

const STATUS_CLASS: Record<GoodsReceiptStatus, string> = {
  draft: "border-border bg-muted/40 text-foreground",
  confirmed: "border-leaf/40 bg-leaf/10 text-leaf",
  void: "border-destructive/30 bg-destructive/10 text-destructive",
};

const CARRIER_LABELS: Record<string, string> = {
  hung_arun: "ຮຸ່ງອາລຸນ",
  anusith: "ອານຸສິດ",
  other: "ອື່ນໆ",
};

export function InventoryReceiptsPage() {
  const nav = useNavigate();
  const [statusFilter, setStatusFilter] = useState<
    GoodsReceiptStatus | "all"
  >(() => {
    if (typeof window === "undefined") return "all";
    const status = new URLSearchParams(window.location.search).get("status");
    if (status === "draft" || status === "confirmed" || status === "void") {
      return status;
    }
    return "all";
  });
  const receipts = useGoodsReceiptsQuery(
    statusFilter === "all" ? undefined : { status: statusFilter },
  );
  const deleteReceipt = useDeleteGoodsReceipt();
  const duplicateReceipt = useDuplicateGoodsReceipt();
  const canWrite = useActionPermission("inventory:write");

  const rows = receipts.data?.data ?? [];

  async function handleDeleteReceipt(
    receipt: {
      id: string;
      receiptNumber: string;
      status: GoodsReceiptStatus;
    },
    e: React.MouseEvent,
  ) {
    e.stopPropagation();
    const isConfirmed = receipt.status === "confirmed";
    const ok = await confirm({
      title: "ລຶບໃບຮັບເຂົ້າ?",
      description: isConfirmed
        ? `${receipt.receiptNumber} — ສະຕັອກ/lot ຈະຖືກຫັກຄືນ (ຖ້າຍັງບໍ່ຖືກຂາຍ)`
        : `${receipt.receiptNumber} — ໃບຮ່າງນີ້ຈະຖືກລຶບຖາວອນ`,
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await deleteReceipt.mutateAsync(receipt.id);
      toast.success("ລຶບໃບຮັບເຂົ້າສຳເລັດ");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "ລຶບລົ້ມເຫຼວ");
    }
  }

  async function handleDuplicateReceipt(
    receipt: {
      id: string;
      receiptNumber: string;
    },
    e: React.MouseEvent,
  ) {
    e.stopPropagation();
    const ok = await confirm({
      title: "ຄັດລອກໃບຮັບເຂົ້າ?",
      description: `${receipt.receiptNumber} — ຈະສ້າງເປັນໃບ draft ໃໝ່ ພ້ອມລາຍການເດີມ`,
      actionText: "ຄັດລອກ",
    });
    if (!ok) return;
    try {
      const created = await duplicateReceipt.mutateAsync(receipt.id);
      toast.success(`ສ້າງ draft ໃໝ່ ${created.receiptNumber} ແລ້ວ`);
      nav({
        to: "/app/inventory/receive/$id/edit",
        params: { id: created.id },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "ຄັດລອກລົ້ມເຫຼວ");
    }
  }

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ຮັບເຂົ້າສະຕັອກ
            </h2>
            <p className="text-muted-foreground text-sm">
              ໃບຮັບເຂົ້າສິນຄ້າ (lot) ພ້ອມຕົ້ນທຸນ + ຄ່າຂົນສົ່ງ
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          {canWrite ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => nav({ to: "/app/inventory/imports" })}
              >
                ສັ່ງນຳເຂົ້າ
              </Button>
              <Button
                className="rounded-full"
                onClick={() => nav({ to: "/app/inventory/receive" })}
              >
                <Plus data-icon="inline-start" />
                ຮັບເຂົ້າໃໝ່
              </Button>
            </div>
          ) : null}
        </div>

        <InventoryPlanningPanel listLimit={6} />

        <div className="mt-4 flex flex-wrap gap-2">
          {(
            [
              { value: "all" as const, label: "ທັງໝົດ" },
              { value: "draft" as const, label: STATUS_LABELS.draft },
              { value: "confirmed" as const, label: STATUS_LABELS.confirmed },
            ] as const
          ).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatusFilter(opt.value)}
              className={
                statusFilter === opt.value
                  ? "rounded-full border border-secondary bg-secondary px-3 py-1.5 text-secondary-foreground text-sm"
                  : "rounded-full border border-secondary/30 px-3 py-1.5 text-sm hover:bg-secondary/10"
              }
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="mt-2 flex flex-col gap-1">
          <h3 className="font-display text-lg">ໃບຮັບເຂົ້າລ່າສຸດ</h3>
          <p className="text-muted-foreground text-sm">
            lot ທີ່ບັນທຶກແລ້ວ ພ້ອມຕົ້ນທຸນ landed
          </p>
        </div>

        {receipts.isLoading ? (
          <div className="flex flex-col gap-3">
            {["a", "b", "c"].map((id) => (
              <Skeleton key={id} className="h-20 rounded-xl" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="brand-card p-8">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Package />
                </EmptyMedia>
                <EmptyTitle>ຍັງບໍ່ມີໃບຮັບເຂົ້າ</EmptyTitle>
                <EmptyDescription>
                  ສ້າງໃບຮັບເຂົ້າເມື່ອສິນຄ້າຮອດຮ້ານ (ຈາກຂົນສົ່ງ)
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {rows.map((r) => (
              <li
                key={r.id}
                className="brand-card flex items-stretch gap-1 p-1 transition-colors hover:border-secondary/50"
              >
                <button
                  type="button"
                  onClick={() =>
                    nav({
                      to: "/app/inventory/receive/$id",
                      params: { id: r.id },
                    })
                  }
                  className="flex min-w-0 flex-1 flex-col gap-2 p-3 text-left"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm tabular-nums">
                      {r.receiptNumber}
                    </span>
                    <Badge variant="outline" className={STATUS_CLASS[r.status]}>
                      {STATUS_LABELS[r.status]}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground text-xs">
                    <span>{formatDateTimeLocal(r.receivedAt)}</span>
                    {r.carrier ? (
                      <span>{CARRIER_LABELS[r.carrier] ?? r.carrier}</span>
                    ) : null}
                    <span>
                      {r.lineCount} ລາຍການ ·{" "}
                      {formatQtyWithUnit(
                        r.totalStockQuantity ?? r.totalQuantity,
                        "ຊິ້ນ",
                      )}{" "}
                      ເຂົ້າສະຕັອກ
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground text-xs">
                      ຕົ້ນທຸນລວມ (landed)
                    </span>
                    <span className="font-semibold text-secondary tabular-nums">
                      {formatLak(
                        r.status === "confirmed"
                          ? r.landedSubtotal
                          : r.purchaseSubtotal,
                      )}
                    </span>
                  </div>
                </button>
                {canWrite ? (
                  <div className="my-2 mr-1 flex shrink-0 flex-col gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`ຄັດລອກ ${r.receiptNumber}`}
                      disabled={duplicateReceipt.isPending}
                      onClick={(e) => handleDuplicateReceipt(r, e)}
                    >
                      <Copy />
                    </Button>
                    {r.status !== "void" ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        aria-label={`ລຶບ ${r.receiptNumber}`}
                        disabled={deleteReceipt.isPending}
                        onClick={(e) => handleDeleteReceipt(r, e)}
                      >
                        <TrashIcon />
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Main>
    </>
  );
}
